import React, { useEffect, useMemo } from 'react';
import { useMyAppointmentsLocale } from '../../pages/MyAppointments';
import {
  upcomingAppointmentsOffset,
  useUpcomingAppointments,
} from '../UpcomingAppointments';
import UpcomingAppointmentCard from './UpcomingAppointmentCard';
import PaginateBlock from 'components/PaginateBlock';
import EmptyStateComponent from 'components/EmptyStateComponent';
import ButtonComponent from 'components/button/buttonComponent';
import {
  LEGAL_DOCUMENTS_ROUTE,
  MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT,
} from 'utilities/routes';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import CancelAppointmentModal from '../modalContent/CancelAppointmentModal';
import Loader from 'components/loaderComponent';
import { FIND_A_PROVIDER } from '../scheduled-a-appointment/routePath';
import { Hasura_Legal_Document_Types_Enum } from 'graphql/generated/hasura';

const UpcomingAppointmentsWrapper = () => {
  const { locale, loading: localeLoading } = useMyAppointmentsLocale();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const offset = queryParams.get(upcomingAppointmentsOffset);
  const navigate = useNavigate();

  const {
    data: upcomingAppointments,
    total,
    variables,
    setVars,
    loading,
  } = useUpcomingAppointments();

  console.log(upcomingAppointments);

  useEffect(() => {
    if (!!offset && variables.offset !== Number(offset)) {
      setVars((vars) => ({ ...vars, offset: Number(offset || 0) }));
    }
  }, [offset, setVars, variables.offset]);

  useEffect(() => {
    if (variables.offset >= total) {
      setVars((vars) => ({ ...vars, offset: 0 }));
    }
  }, [offset, setVars, total, variables.limit, variables.offset]);

  const paginateSubtitle = useMemo(
    () =>
      `${locale?.showing} ${variables.offset + 1}-${Math.min(
        variables.offset + variables.limit,
        total,
      )} ${locale?.appointments} ${locale?.from} ${total}`,
    [locale, total, variables],
  );

  const hasShowPaginate = useMemo(
    () => total > variables.limit,
    [total, variables.limit],
  );

  const setNewOffset = (offset: number) =>
    setVars((vars) => ({ ...vars, offset }));

  const handleScheduleAnAppointment = () => {
    navigate(`${MY_APPOINTMENTS_SCHEDULE_AN_APPOINTMENT}/${FIND_A_PROVIDER}`);
  };

  if (loading || localeLoading) {
    return <Loader testID={'upcoming-appointments-loader'} />;
  }

  if (!upcomingAppointments?.length) {
    return (
      <div
        className="desktop:p-8 p-5 bg-white rounded-xl flex flex-col gap-8"
        data-testid="upcoming-appointments-card"
        data-tour="upcoming-appointments"
      >
        <div className="flex items-center max-desktop:justify-between gap-4">
          <h3 className="desktop:text-h3 text-h4 text-neutral-800 font-semibold">
            {locale.upcomingAppointmentsCardTitle}
          </h3>
          <span className="text-neutral-800 text-sm font-semibold rounded-md px-2 py-1 bg-[rgba(0,0,0,.05)]">
            {total}
          </span>
          <Link
            target="_blank"
            to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.CancellationPolicy}`}
            className={'text-clc-blue underline ml-auto'}
          >
            Cancellation and Late Arrival Policy
          </Link>
        </div>
        <div className="desktop:px-7 px-0 flex grow justify-center items-center">
          <EmptyStateComponent
            ctaButton={
              <ButtonComponent
                testID={'schedule-appointment'}
                className="!text-sm rounded mt-12 desktop:mt-8"
                onClick={handleScheduleAnAppointment}
              >
                {locale.scheduleAnAppointment}
              </ButtonComponent>
            }
            className="min-h-0"
            titleClassName="desktop:font-medium text-center text-h3 desktop:text-h2 font-semibold"
            textClassName="text-center"
            title={locale.noResultsTitle}
            text={locale.noResults}
            testID={'upcoming-appointments-empty'}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      data-tour="upcoming-appointments"
      className="bg-white rounded-xl desktop:p-8 p-5 desktop:gap-8 gep-2.5"
    >
      <div className="flex items-center justify-between desktop:justify-start gap-4">
        <h3 className="text-xl desktop:text-h3 text-neutral-800  font-semibold">
          {locale.upcomingAppointmentsCardTitle}
        </h3>
        <span className="text-neutral-800 text-sm font-semibold rounded-md px-2 py-1 bg-[rgba(0,0,0,.05)]">
          {total}
        </span>

        <Link
          target="_blank"
          to={`${LEGAL_DOCUMENTS_ROUTE}/${Hasura_Legal_Document_Types_Enum.CancellationPolicy}`}
          className={
            'text-clc-blue underline ml-auto uppercase font-semibold text-sm'
          }
        >
          Cancellation and Late Arrival Policy
        </Link>
      </div>

      <div className="flex flex-col mt-4 gap-4">
        {upcomingAppointments.map((appointment) => (
          <UpcomingAppointmentCard
            appointment={appointment}
            key={appointment.appointmentId}
          />
        ))}
      </div>

      {hasShowPaginate && (
        <div className="flex max-desktop:flex-col gap-2.5 justify-between items-center mt-2">
          <p className="font-medium text-sm text-med-gray-3">
            {paginateSubtitle}
          </p>
          <PaginateBlock
            itemsPerPage={variables.limit}
            totalItems={total}
            setNewOffset={setNewOffset}
            offsetParams={upcomingAppointmentsOffset}
          />
        </div>
      )}

      <CancelAppointmentModal />
    </div>
  );
};

export default UpcomingAppointmentsWrapper;
